export type UpdateFieldOptions<T> = {
  onChange?: (key: keyof T, value: null) => void;
};

/**
 * Applies nextValues on top of prevValues and resets any fields
 * that depend on a changed key. Calls options.onChange for each reset field
 * so the caller can sync the form (e.g. via formApi.setFieldValue).
 */
export const updateFieldDependencies = <T extends Record<string, any>, K extends string = string>(
  prevValues: Partial<T>,
  nextValues: Partial<T>,
  dependencies: Partial<Record<K, Array<K>>>,
  options?: UpdateFieldOptions<T>,
): Partial<T> => {
  const changedKeys = (Object.keys(nextValues) as Array<K>).filter((key) => {
    const next = nextValues[key];
    const prev = prevValues[key];

    // Compare objects by id if available, otherwise by reference
    if (next && prev && typeof next === 'object' && typeof prev === 'object' && 'id' in next && 'id' in prev) {
      return next.id !== prev.id;
    }

    return next !== prev;
  });

  const dependenciesValues = resolveFieldDependencies(changedKeys, dependencies).reduce<Partial<T>>(
    (acc, key) => {
      options?.onChange?.(key, null);
      acc[key] = undefined;
      return acc;
    },
    {} as Partial<T>,
  );

  return { ...prevValues, ...nextValues, ...dependenciesValues };
};

/**
 * Walks field dependencies for each changed key and returns all fields that
 * should be reset — including nested ones (e.g. changing customer resets
 * division, then region, then branch).
 */
export const resolveFieldDependencies = <T extends string>(
  keys: Array<T>,
  dependencies: Partial<Record<T, Array<T>>>,
): Array<T> => {
  const result = new Set<T>();

  const traverse = (key: T) => {
    const deps = dependencies[key] ?? [];
    deps.forEach((dep) => {
      if (!result.has(dep)) {
        result.add(dep);
        traverse(dep);
      }
    });
  };

  keys.forEach(traverse);
  return Array.from(result);
};
